import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type LeaveRequestStatus = "pending" | "approved" | "rejected" | "cancelled";

export interface LeaveRequestEmployeeSummary {
  id: ID;
  fullName: string;
  employeeCode: string | null;
}

export interface LeaveRequestTypeSummary {
  id: ID;
  slug: string;
  title: string;
}

export interface LeaveRequestReviewerSummary {
  id: ID;
  name: string;
  email: string;
}

export interface LeaveRequestRecord {
  id: ID;
  employeeId: ID;
  leaveTypeId: ID;
  leaveYearId: ID | null;
  startDate: string;
  endDate: string;
  days: string;
  isPaid: boolean;
  reason: string | null;
  submittedBy: ID | null;
  status: LeaveRequestStatus;
  reviewedBy: ID | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
  employee?: LeaveRequestEmployeeSummary;
  leaveType?: LeaveRequestTypeSummary;
  reviewer?: LeaveRequestReviewerSummary;
}

const LS_KEY = "inventory.leaveRequests.v1";

type LeaveRequestApiRecord = {
  id?: number | string;
  employee_id?: number | string;
  leave_type_id?: number | string;
  leave_year_id?: number | string | null;
  start_date?: string | null;
  end_date?: string | null;
  days?: string | number | null;
  is_paid?: boolean | number | string | null;
  reason?: string | null;
  submitted_by?: number | string | null;
  status?: string | null;
  reviewed_by?: number | string | null;
  reviewed_at?: string | null;
  review_note?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  employee?: { id?: number | string; full_name?: string; employee_code?: string | null };
  leave_type?: { id?: number | string; slug?: string; title?: string };
  reviewer?: { id?: number | string; name?: string; email?: string };
};

type UpsertApiResponse = { message?: string; data?: LeaveRequestApiRecord };

function dedupeById(items: LeaveRequestRecord[]): LeaveRequestRecord[] {
  const byId = new Map<ID, LeaveRequestRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function normalizeStatus(raw: string | null | undefined): LeaveRequestStatus {
  const s = (raw ?? "").toLowerCase();
  if (s === "approved" || s === "rejected" || s === "cancelled") return s;
  return "pending";
}

function mapApiRecord(record: LeaveRequestApiRecord): LeaveRequestRecord {
  const employee = record.employee;
  const leaveType = record.leave_type;
  const reviewer = record.reviewer;
  return {
    id: String(record.id ?? ""),
    employeeId: String(record.employee_id ?? ""),
    leaveTypeId: String(record.leave_type_id ?? ""),
    leaveYearId: record.leave_year_id != null && String(record.leave_year_id) !== "" ? String(record.leave_year_id) : null,
    startDate: String(record.start_date ?? "").slice(0, 10),
    endDate: String(record.end_date ?? "").slice(0, 10),
    days: String(record.days ?? ""),
    isPaid: record.is_paid === undefined || record.is_paid === null ? true : Boolean(record.is_paid),
    reason: record.reason ?? null,
    submittedBy: record.submitted_by != null && String(record.submitted_by) !== "" ? String(record.submitted_by) : null,
    status: normalizeStatus(record.status),
    reviewedBy: record.reviewed_by != null && String(record.reviewed_by) !== "" ? String(record.reviewed_by) : null,
    reviewedAt: record.reviewed_at ?? null,
    reviewNote: record.review_note ?? null,
    createdAt: record.created_at ?? new Date().toISOString(),
    updatedAt: record.updated_at ?? record.created_at ?? new Date().toISOString(),
    employee:
      employee && employee.id != null
        ? {
            id: String(employee.id),
            fullName: String(employee.full_name ?? "").trim() || "—",
            employeeCode: employee.employee_code != null ? String(employee.employee_code) : null,
          }
        : undefined,
    leaveType:
      leaveType && leaveType.id != null
        ? {
            id: String(leaveType.id),
            slug: String(leaveType.slug ?? ""),
            title: String(leaveType.title ?? ""),
          }
        : undefined,
    reviewer:
      reviewer && reviewer.id != null
        ? {
            id: String(reviewer.id),
            name: String(reviewer.name ?? ""),
            email: String(reviewer.email ?? ""),
          }
        : undefined,
  };
}

function load(): LeaveRequestRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LeaveRequestRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: LeaveRequestRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): LeaveRequestApiRecord[] {
  if (Array.isArray(body)) return body as LeaveRequestApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const fromBlock = (block: unknown): LeaveRequestApiRecord[] | null => {
    if (Array.isArray(block)) return block as LeaveRequestApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as LeaveRequestApiRecord[];
    }
    return null;
  };
  for (const key of ["data", "items", "leave_requests", "results"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractFromUpsertBody(body: unknown): LeaveRequestApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data) && "id" in data) {
    return data as LeaveRequestApiRecord;
  }
  return null;
}

export function leaveRequestErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export function listLeaveRequests(): LeaveRequestRecord[] {
  return load().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function listLeaveRequestsForEmployee(employeeId: ID): LeaveRequestRecord[] {
  return listLeaveRequests().filter((r) => r.employeeId === employeeId);
}

export function getLeaveRequestById(id: ID): LeaveRequestRecord | undefined {
  return load().find((r) => r.id === id);
}

export function countLeaveRequestsForLeaveType(leaveTypeId: ID): number {
  return load().filter((r) => r.leaveTypeId === leaveTypeId).length;
}

const PER_PAGE = 50;

export async function syncLeaveRequestsFromApi(filters?: {
  employee_id?: ID;
  status?: LeaveRequestStatus;
}): Promise<LeaveRequestRecord[]> {
  const collected: LeaveRequestApiRecord[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    const params: Record<string, string | number> = { page, per_page: PER_PAGE };
    if (filters?.employee_id) params.employee_id = filters.employee_id;
    if (filters?.status) params.status = filters.status;
    const response = await api.get("/api/v1/leave-requests", { params });
    const body = response.data;
    collected.push(...extractRowsFromResponse(body));
    lastPage = readLastPageFromListBody(body) ?? 1;
    page += 1;
  } while (page <= lastPage && page <= 100);

  const mapped = collected.map(mapApiRecord).filter((r) => Boolean(r.id));

  if (filters?.employee_id) {
    const rest = load().filter((r) => r.employeeId !== filters.employee_id);
    save(dedupeById([...rest, ...mapped]));
  } else {
    save(dedupeById(mapped));
  }

  return listLeaveRequests();
}

export async function createLeaveRequest(data: {
  employeeId: ID;
  leaveTypeId: ID;
  startDate: string;
  endDate: string;
  isPaid?: boolean;
  reason?: string | null;
}): Promise<{ record: LeaveRequestRecord; message: string }> {
  const payload = {
    employee_id: data.employeeId,
    leave_type_id: data.leaveTypeId,
    start_date: data.startDate,
    end_date: data.endDate,
    is_paid: data.isPaid !== false,
    reason: data.reason?.trim() || null,
  };
  const response = await api.post<UpsertApiResponse>("/api/v1/leave-requests", payload);
  const created = extractFromUpsertBody(response.data);
  if (!created?.id) throw new Error("Invalid create leave request response.");
  const record = mapApiRecord(created);
  const items = load().filter((r) => r.id !== record.id);
  items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Leave request submitted." };
}

export async function updateLeaveRequest(
  id: ID,
  data: Partial<{
    employeeId: ID;
    leaveTypeId: ID;
    startDate: string;
    endDate: string;
    isPaid: boolean;
    reason: string | null;
  }>,
): Promise<{ record: LeaveRequestRecord; message: string } | null> {
  const items = load();
  const idx = items.findIndex((r) => r.id === id);
  if (idx === -1) return null;
  const payload: Record<string, unknown> = {};
  if (data.employeeId != null) payload.employee_id = data.employeeId;
  if (data.leaveTypeId != null) payload.leave_type_id = data.leaveTypeId;
  if (data.startDate != null) payload.start_date = data.startDate;
  if (data.endDate != null) payload.end_date = data.endDate;
  if (data.isPaid !== undefined) payload.is_paid = Boolean(data.isPaid);
  if (data.reason !== undefined) payload.reason = data.reason?.trim() || null;

  const response = await api.put<UpsertApiResponse>(`/api/v1/leave-requests/${id}`, payload);
  const updated = extractFromUpsertBody(response.data);
  if (!updated?.id) throw new Error("Invalid update leave request response.");
  items[idx] = mapApiRecord(updated);
  save(items);
  return { record: items[idx], message: response.data?.message || "Leave request updated." };
}

export async function approveLeaveRequest(
  id: ID,
  reviewNote?: string | null,
): Promise<{ record: LeaveRequestRecord; message: string }> {
  const response = await api.post<UpsertApiResponse>(`/api/v1/leave-requests/${id}/approve`, {
    review_note: reviewNote?.trim() || null,
  });
  const updated = extractFromUpsertBody(response.data);
  if (!updated?.id) throw new Error("Invalid approve response.");
  const record = mapApiRecord(updated);
  const items = load();
  const i = items.findIndex((r) => r.id === id);
  if (i >= 0) items[i] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Approved." };
}

export async function rejectLeaveRequest(
  id: ID,
  reviewNote?: string | null,
): Promise<{ record: LeaveRequestRecord; message: string }> {
  const response = await api.post<UpsertApiResponse>(`/api/v1/leave-requests/${id}/reject`, {
    review_note: reviewNote?.trim() || null,
  });
  const updated = extractFromUpsertBody(response.data);
  if (!updated?.id) throw new Error("Invalid reject response.");
  const record = mapApiRecord(updated);
  const items = load();
  const i = items.findIndex((r) => r.id === id);
  if (i >= 0) items[i] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Rejected." };
}

export async function cancelLeaveRequest(id: ID): Promise<{ record: LeaveRequestRecord; message: string }> {
  const response = await api.post<UpsertApiResponse>(`/api/v1/leave-requests/${id}/cancel`);
  const updated = extractFromUpsertBody(response.data);
  if (!updated?.id) throw new Error("Invalid cancel response.");
  const record = mapApiRecord(updated);
  const items = load();
  const i = items.findIndex((r) => r.id === id);
  if (i >= 0) items[i] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Cancelled." };
}
